import store from 'app/store/store';
import { getLessonItems, getLessonItemV3 } from 'core/crud/lesson.crud';
import { validateLessonItem } from 'core/models/lessonItem.model';
import { selectors as organizationSelectors } from 'core/store/organization.duck';
import { selectors as systemSettingsSelectors } from 'core/store/systemSettings.duck';
import { getGuidCourseByTemplate } from 'core/utils/course';
import {
  addUserInfoToJob,
  canUseTangerineViewer,
  canUseTangerineViewerInPreview,
  getAssignedUsersToLessonItem,
  getIconByContentType,
  getRouteProjectionByContentType,
  getTypeNameByContentType,
  getUsePdfTron,
  isDueDate,
} from 'core/utils/lessonItems';
import { isStudent } from 'core/utils/user';
import { CLIENT } from 'core/variables/constant';
import { LESSON_ITEM_STATUS } from 'core/variables/lessonItems';
import i18next from 'i18next';

function normalizeJob(job) {
  if (!job) return null;
  return job?.id ? { ...job, guid: job.id } : job;
}

async function getStudentJob(jobs, lessonItemGuid, courseGuid) {
  const studentJob = normalizeJob(jobs?.[0]);
  if (studentJob) return studentJob;
  const v2Response = await getLessonItems(lessonItemGuid, courseGuid);
  return normalizeJob(v2Response?.data?.data?.job) ?? null;
}

export async function getLessonItemService(lessonItemGuid, lessonGuid, course, currentUser) {
  // Lectura vía selectores para que el override dev-only de tenant (`__tenantOverride`) alcance esta rama.
  const state = store.getState();
  const enableAdvancedProgramming = systemSettingsSelectors.getSystemSettings(state)?.enable_advanced_programming === 1;
  const client = organizationSelectors.getClient(state);
  if (
    enableAdvancedProgramming ||
    client === CLIENT.TANGERINE ||
    client === CLIENT.SANTILLANA_BRASIL_DEMO ||
    client === CLIENT.SANTILLANA_BRASIL_COMPARTILHA ||
    client === CLIENT.SANTILLANA_BRASIL_UNOI_ON_PREM ||
    client === CLIENT.SANTILLANA_BRASIL_COMPARTILHA_ON_PREM
  ) {
    const courseGuid = course?.guid;
    try {
      const { data } = await getLessonItemV3(lessonItemGuid, lessonGuid, courseGuid);
      const job = isStudent(currentUser) ? await getStudentJob(data?.data?.jobs, lessonItemGuid, courseGuid) : null;

      return {
        ...validateLessonItem(data?.data),
        assignedUsers: getAssignedUsersToLessonItem(data?.data, course),
        allUsers: addUserInfoToJob(data?.data?.jobs, course?.users, data?.data?.lesson_item_guid, courseGuid),
        jobs: addUserInfoToJob(data?.data?.jobs, getAssignedUsersToLessonItem(data?.data, course), data?.data?.lesson_item_guid, courseGuid),
        job,
        icon: getIconByContentType(data?.data?.content_type_guid),
        typeName: data?.data?.content_type_guid ? i18next.t(`lesson:${getTypeNameByContentType(data?.data?.content_type_guid)}`) : '',
        routeProjection: getRouteProjectionByContentType(data?.data?.content_guid, data?.data?.content_type_guid),
        usePdfTron: getUsePdfTron(data?.data?.content_type_guid),
        isDueDate: isDueDate(data?.data?.content_type_guid),
        useTangerineViewer: canUseTangerineViewer(data?.data?.content_type_guid),
        useTangerineViewerInPreview: canUseTangerineViewerInPreview(data?.data?.content_type_guid),
      };
    } catch (error) {
      return Promise.reject({
        status: error?.response?.status || 500,
        message: 'error',
        error,
      });
    }
  } else {
    const response = await getLessonItems(lessonItemGuid, course?.guid);
    const data = response?.data?.data;
    // Mismo saneado que hace `getLessonItems` antes de llamar a la API: sin el `?` y los query params.
    const requestedGuid = lessonItemGuid?.split('?')[0];
    const resolvedLessonItemGuid = data?.guid ?? requestedGuid;
    const assignedUsers = getAssignedUsersToLessonItem(data, course);
    const courseGuidByTemplate = getGuidCourseByTemplate(course);
    return {
      ...data,
      // La API v2 identifica el item con `guid` y trae el job del alumno en `job`: se iguala el contrato
      // de la rama v3 (`lesson_item_guid`, `jobStatus`) que consumen navegación y estado (TAN-10512).
      lesson_item_guid: resolvedLessonItemGuid,
      guid: resolvedLessonItemGuid,
      jobStatus: data?.job?.status || LESSON_ITEM_STATUS.PENDING,
      assignedUsers,
      allUsers: addUserInfoToJob(data?.jobs, course?.users, resolvedLessonItemGuid, courseGuidByTemplate),
      jobs: addUserInfoToJob(data?.jobs, assignedUsers, resolvedLessonItemGuid, courseGuidByTemplate),
    };
  }
}
